import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowRight, Check, Calculator, Users, FileCheck, ShieldCheck, Scale,
  Handshake, MessageSquare, Repeat, Compass, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';
import { SmsOptInCheckbox } from '@/components/SmsConsentDisclosure';

const GOLD = '#BCA480';
const DARK = '#434345';

// -----------------------------------------------------------------------------
// Small atoms (match /property-management visual language)
// -----------------------------------------------------------------------------

const Eyebrow = ({ children }) => (
  <div
    className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-semibold"
    style={{ color: GOLD }}
  >
    <span className="w-6 h-px" style={{ background: GOLD }} />
    {children}
  </div>
);

const SectionHeading = ({ eyebrow, title, kicker, dark = false, center = false }) => (
  <div className={`mb-10 ${center ? 'mx-auto text-center max-w-3xl' : 'max-w-3xl'}`}>
    {eyebrow && (
      <div className={`mb-4 ${center ? 'flex justify-center' : ''}`}>
        <Eyebrow>{eyebrow}</Eyebrow>
      </div>
    )}
    <h2
      className={`text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight ${dark ? 'text-white' : ''}`}
      style={dark ? { color: '#ffffff' } : { color: DARK }}
    >
      {title}
    </h2>
    {kicker && (
      <p className={`mt-4 text-base sm:text-lg leading-relaxed ${dark ? 'text-white/75' : 'text-gray-600'}`}>
        {kicker}
      </p>
    )}
  </div>
);

// -----------------------------------------------------------------------------

const HoaManagement = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    association_name: '',
    location: '',
    unit_count: '',
    association_type: '',
    current_management_status: '',
    annual_budget: '',
    amenities: '',
    desired_services: [],
    current_challenges: '',
    desired_transition_date: '',
    sms_opt_in: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (s) => {
    setForm((f) => ({
      ...f,
      desired_services: f.desired_services.includes(s)
        ? f.desired_services.filter((x) => x !== s)
        : [...f.desired_services, s],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/leads/hoa-inquiry', form);
      toast.success("Thanks — we've received your request. Benjamin will reach out within 1 business day.");
      setForm({
        name: '', email: '', phone: '',
        association_name: '', location: '', unit_count: '',
        association_type: '', current_management_status: '', annual_budget: '',
        amenities: '', desired_services: [], current_challenges: '',
        desired_transition_date: '', sms_opt_in: false,
      });
    } catch (err) {
      toast.error("Failed to submit. Please try again or call 901-390-4414.");
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    {
      icon: Calculator,
      title: 'Accounting & Financial Management',
      body: 'Operating and reserve bank accounts, invoice processing, monthly accounting reports, bank reconciliations, and open access to records for board members.',
    },
    {
      icon: Users,
      title: 'Meeting Support',
      body: 'Annual, board, vendor, and committee meetings — attended and supported so your board can lead the conversation, not manage logistics.',
    },
    {
      icon: FileCheck,
      title: 'Architectural Review Requests',
      body: 'Monitor compliance against your governing documents, administer ARC applications, and act as the liaison between homeowners and the committee.',
    },
    {
      icon: Scale,
      title: 'Covenant & Rule Enforcement',
      body: 'Fair, consistent, and documented enforcement of your covenants and rules — the kind that holds up when it needs to.',
    },
    {
      icon: ShieldCheck,
      title: 'Collections & Delinquency Management',
      body: 'Clear assessment collections workflow, documented delinquency escalation, and coordination with association counsel when it becomes necessary.',
    },
  ];

  const pillars = [
    {
      icon: ShieldCheck,
      title: 'Steward',
      body: 'Protect the community\'s finances, reputation, and physical assets as if they were our own.',
    },
    {
      icon: Handshake,
      title: 'Facilitate',
      body: 'Make board service easier, not harder. Volunteer boards deserve a partner, not a vendor.',
    },
    {
      icon: MessageSquare,
      title: 'Communicate',
      body: 'Transparent reporting for the board. Clear, respectful communication for homeowners.',
    },
    {
      icon: Repeat,
      title: 'Enforce (Fairly)',
      body: 'Consistent, documented application of the governing documents — never arbitrary, never personal.',
    },
  ];

  const processSteps = [
    {
      n: '01',
      title: 'Discovery',
      body: 'A no-cost conversation with your board about the community, the challenges, and what good management should look like.',
    },
    {
      n: '02',
      title: 'Proposal',
      body: 'A written scope of services and transparent pricing tailored to your association — not a template.',
    },
    {
      n: '03',
      title: 'Transition',
      body: 'A structured handoff from your current manager (or self-management): records, vendor relationships, bank accounts, and communications.',
    },
    {
      n: '04',
      title: 'Steady-state',
      body: 'Monthly financials, meetings supported, requests processed, enforcement applied consistently — with your board in the drivers seat.',
    },
  ];

  const serviceOptions = services.map((s) => s.title);

  return (
    <div className="min-h-screen bg-[#f9f7f3]" data-testid="hoa-management-page">
      <SEO
        title="HOA & Condo Association Management in Memphis & North Mississippi | Bespoke Realty"
        description="Boutique HOA, condo, and POA management serving Memphis, Southaven, Olive Branch, Hernando, Collierville, and Germantown. Accounting, meeting support, architectural review, covenant enforcement, and collections — built around your board. Request a proposal."
        keywords="HOA management Memphis, condo association management Tennessee, POA management Mississippi, Memphis HOA manager, condo management Southaven, association management Olive Branch, DeSoto County HOA, board of directors HOA Memphis"
      />
      <StructuredData schema={[
        schemas.service({
          name: 'HOA & Association Management',
          serviceType: 'PropertyManagement',
          description: 'Boutique association management for HOAs, condo associations, and POAs. Financial transparency, meeting support, architectural review, covenant enforcement, collections, and homeowner portal — purpose-built for volunteer boards.',
        }),
        schemas.breadcrumb([{ name: 'Home', url: '/' }, { name: 'HOA Management', url: '/hoa-management' }]),
      ]} />

      {/* ---------- HERO ---------- */}
      <section className="relative h-[72vh] min-h-[560px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-10"
             style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.60) 50%, rgba(0,0,0,0.72) 100%)' }} />
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=2400&q=80)',
          }}
        />
        <div className="relative z-20 w-full max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="mb-5">
            <span
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm"
              style={{ background: `${GOLD}25`, color: '#f6ecd6', border: `1px solid ${GOLD}55` }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
              For HOA, Condo &amp; POA Boards
            </span>
          </div>
          <h1
            className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
            style={{ color: '#ffffff' }}
            data-testid="hoa-heading"
          >
            <span style={{ color: '#ffffff' }}>Association Management<br />Built Around </span>
            <span style={{ color: GOLD }}>Your Board.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto mb-8">
            Volunteer boards deserve a management partner — not another vendor. Bespoke Realty's
            Association Management brings the same investment-focused philosophy, local presence,
            and transparent service that defines our brokerage.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#hoa-proposal"
              className="inline-flex items-center justify-center gap-2 text-white px-6 py-3.5 rounded-md font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: GOLD }}
              data-testid="hoa-hero-cta-proposal"
            >
              Request a Proposal <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#hoa-services"
              className="inline-flex items-center justify-center gap-2 border border-white/50 text-white hover:bg-white/10 px-6 py-3.5 rounded-md font-semibold transition-colors"
              data-testid="hoa-hero-cta-services"
            >
              See Our Services
            </a>
          </div>
        </div>
      </section>

      {/* ---------- OPENING STATEMENT ---------- */}
      <section className="py-24" data-testid="hoa-opening">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>Why Bespoke</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" style={{ color: DARK }}>
              Your board shouldn't feel like<br />
              <span style={{ color: GOLD }}>a second job.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-2">
            <p className="text-lg leading-relaxed text-gray-700 mb-5">
              Volunteer boards carry real responsibility — budgets, vendors, compliance, neighbor disputes,
              reserve planning — on top of their day jobs. A good management company lifts that weight.
              A great one makes the community stronger for having been there.
            </p>
            <p className="text-lg leading-relaxed text-gray-700">
              We bring the brokerage discipline you'd expect from Bespoke Realty — financial transparency,
              documentation, consistent process — into the association space. No templated service, no
              hidden markups, no board left guessing.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- SERVICES ---------- */}
      <section id="hoa-services" className="py-24 scroll-mt-24 border-y border-[#e6dec7]/60" style={{ backgroundColor: '#f4efe6' }} data-testid="hoa-services">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Our Services"
            title="What association management actually looks like."
            kicker="Five service areas, delivered with the same transparency and documentation that defines everything else we do."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div
                key={s.title}
                className="bg-white rounded-lg p-7 border border-[#e6dec7] hover:border-[#BCA480] hover:shadow-md transition-all"
                data-testid={`hoa-service-${s.title.toLowerCase().replace(/[^a-z]+/g, '-').slice(0, 30)}`}
              >
                <s.icon className="h-8 w-8 mb-4" style={{ color: GOLD }} strokeWidth={1.5} />
                <h3 className="text-lg font-bold mb-2" style={{ color: DARK }}>{s.title}</h3>
                <p className="text-[14.5px] text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FOUR PILLARS (Steward/Facilitate/Communicate/Enforce) ---------- */}
      <section className="py-24 bg-white" data-testid="hoa-pillars">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Our Approach"
            title="The four principles behind every community we manage."
            kicker="Every decision we make for an association runs through these four lenses."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#e6dec7]/60 rounded-lg overflow-hidden">
            {pillars.map((p) => (
              <div key={p.title} className="bg-[#faf8f4] p-8 hover:bg-white transition-colors" data-testid={`hoa-pillar-${p.title.toLowerCase()}`}>
                <p.icon className="h-8 w-8 mb-5" style={{ color: GOLD }} strokeWidth={1.5} />
                <div className="text-xs tracking-[0.2em] uppercase font-semibold mb-2" style={{ color: GOLD }}>
                  {p.title}
                </div>
                <p className="text-[15px] leading-relaxed" style={{ color: DARK }}>
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- PROCESS ---------- */}
      <section className="py-24" style={{ backgroundColor: DARK }} data-testid="hoa-process">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Transition Process"
            title="From first conversation to steady-state."
            kicker="A deliberate, low-friction path from your current situation to a well-run community — no surprises, no scramble."
            dark
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((s) => (
              <div
                key={s.n}
                className="relative border-t-2 pt-6"
                style={{ borderColor: GOLD }}
                data-testid={`hoa-step-${s.n}`}
              >
                <div className="text-xs font-bold tracking-[0.22em]" style={{ color: GOLD }}>{s.n}</div>
                <h3 className="text-xl font-bold text-white mt-2 mb-2">{s.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FOR WHO ---------- */}
      <section className="py-24 bg-white" data-testid="hoa-for-who">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Who We Serve"
            title="Associations we're built to manage."
            center
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
            {[
              { icon: Building2, label: 'Neighborhood HOAs', body: 'Single-family subdivisions and planned communities with common areas, amenities, or covenant enforcement needs.' },
              { icon: Building2, label: 'Condo Associations', body: 'Buildings and multi-unit developments with shared structural, insurance, and reserve responsibilities.' },
              { icon: Building2, label: 'Property Owners Associations (POAs)', body: 'Larger-scale master associations covering mixed-use or multi-neighborhood developments.' },
            ].map((it) => (
              <div
                key={it.label}
                className="rounded-lg p-7 border border-[#e6dec7]"
                style={{ backgroundColor: '#faf8f4' }}
                data-testid={`hoa-type-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <it.icon className="h-8 w-8 mb-4" style={{ color: GOLD }} strokeWidth={1.5} />
                <h3 className="text-lg font-bold mb-2" style={{ color: DARK }}>{it.label}</h3>
                <p className="text-[14.5px] text-gray-600 leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- PROPOSAL FORM ---------- */}
      <section id="hoa-proposal" className="py-24 scroll-mt-24" style={{ backgroundColor: '#f4efe6' }} data-testid="hoa-proposal-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Request a Proposal"
            title="Tell us about your community."
            kicker="The more detail you share, the more specific our proposal can be. All information is confidential and shared only with Benjamin Wright and our management team."
            center
          />

          <form onSubmit={submit} className="bg-white rounded-lg shadow-lg p-6 sm:p-10 space-y-6" data-testid="hoa-form">

            {/* --- About the community --- */}
            <div>
              <h3 className="text-lg font-bold mb-4 pb-3 border-b border-[#e6dec7]" style={{ color: DARK }}>
                About the Community
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="assoc_name">Community / Association Name *</Label>
                  <Input id="assoc_name" required value={form.association_name}
                         onChange={(e) => handleField('association_name', e.target.value)}
                         data-testid="hoa-input-association-name" />
                </div>
                <div>
                  <Label htmlFor="location">Location (City, State) *</Label>
                  <Input id="location" required placeholder="e.g. Olive Branch, MS"
                         value={form.location}
                         onChange={(e) => handleField('location', e.target.value)}
                         data-testid="hoa-input-location" />
                </div>
                <div>
                  <Label htmlFor="unit_count">Number of Homes / Units *</Label>
                  <Input id="unit_count" required type="number" min="1"
                         value={form.unit_count}
                         onChange={(e) => handleField('unit_count', e.target.value)}
                         data-testid="hoa-input-unit-count" />
                </div>
                <div>
                  <Label>Association Type *</Label>
                  <Select value={form.association_type}
                          onValueChange={(v) => handleField('association_type', v)}>
                    <SelectTrigger data-testid="hoa-select-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HOA">HOA (Homeowners Association)</SelectItem>
                      <SelectItem value="Condo">Condominium Association</SelectItem>
                      <SelectItem value="POA">POA (Property Owners Association)</SelectItem>
                      <SelectItem value="Master">Master / Mixed Association</SelectItem>
                      <SelectItem value="Not sure">Not sure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Current Management Status *</Label>
                  <Select value={form.current_management_status}
                          onValueChange={(v) => handleField('current_management_status', v)}>
                    <SelectTrigger data-testid="hoa-select-status"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Self-managed">Self-managed by the board</SelectItem>
                      <SelectItem value="Looking to switch">Currently using a management company — looking to switch</SelectItem>
                      <SelectItem value="Contract ending">Current contract ending soon</SelectItem>
                      <SelectItem value="New community">Newly formed community</SelectItem>
                      <SelectItem value="Exploring">Just exploring options</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="budget">Approximate Annual Budget (optional)</Label>
                  <Input id="budget" placeholder="e.g. $120,000"
                         value={form.annual_budget}
                         onChange={(e) => handleField('annual_budget', e.target.value)}
                         data-testid="hoa-input-budget" />
                </div>
                <div>
                  <Label htmlFor="transition">Desired Transition Date (optional)</Label>
                  <Input id="transition" type="text" placeholder="e.g. Jan 2027, flexible"
                         value={form.desired_transition_date}
                         onChange={(e) => handleField('desired_transition_date', e.target.value)}
                         data-testid="hoa-input-transition-date" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="amenities">Amenities &amp; Common Areas (optional)</Label>
                  <Input id="amenities" placeholder="e.g. pool, clubhouse, 2 ponds, playground"
                         value={form.amenities}
                         onChange={(e) => handleField('amenities', e.target.value)}
                         data-testid="hoa-input-amenities" />
                </div>
              </div>
            </div>

            {/* --- Desired services --- */}
            <div>
              <h3 className="text-lg font-bold mb-4 pb-3 border-b border-[#e6dec7]" style={{ color: DARK }}>
                Desired Services <span className="text-sm font-normal text-gray-500">(select all that apply)</span>
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {serviceOptions.map((s) => {
                  const active = form.desired_services.includes(s);
                  return (
                    <label
                      key={s}
                      className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${active ? 'border-[#BCA480] bg-[#BCA480]/5' : 'border-gray-200 hover:border-[#BCA480]/50'}`}
                      data-testid={`hoa-service-option-${s.toLowerCase().replace(/[^a-z]+/g, '-').slice(0, 30)}`}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleService(s)}
                        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#BCA480] focus:ring-[#BCA480]"
                      />
                      <span className="text-sm text-gray-700 leading-snug">{s}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* --- Current challenges --- */}
            <div>
              <h3 className="text-lg font-bold mb-4 pb-3 border-b border-[#e6dec7]" style={{ color: DARK }}>
                Current Challenges
              </h3>
              <Label htmlFor="challenges">What's not working today? What would you like to fix?</Label>
              <Textarea
                id="challenges"
                rows={4}
                value={form.current_challenges}
                onChange={(e) => handleField('current_challenges', e.target.value)}
                placeholder="e.g. financial reporting is delayed, architectural requests aren't being tracked, enforcement is inconsistent…"
                data-testid="hoa-input-challenges"
              />
            </div>

            {/* --- Contact --- */}
            <div>
              <h3 className="text-lg font-bold mb-4 pb-3 border-b border-[#e6dec7]" style={{ color: DARK }}>
                Your Contact Info
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-1">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" required value={form.name}
                         onChange={(e) => handleField('name', e.target.value)}
                         data-testid="hoa-input-name" />
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" required type="email" value={form.email}
                         onChange={(e) => handleField('email', e.target.value)}
                         data-testid="hoa-input-email" />
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" required type="tel" value={form.phone}
                         onChange={(e) => handleField('phone', e.target.value)}
                         data-testid="hoa-input-phone" />
                </div>
              </div>
              <SmsOptInCheckbox
                checked={form.sms_opt_in}
                onChange={(v) => handleField('sms_opt_in', v)}
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full text-white py-6 text-base"
              style={{ backgroundColor: GOLD }}
              data-testid="hoa-submit-btn"
            >
              {submitting ? 'Submitting…' : 'Request a Proposal'}
            </Button>
            <p className="text-[11px] text-gray-500 leading-snug text-center">
              All information is confidential and reviewed personally by Benjamin Wright, Principal Broker.
              You'll typically hear back within one business day.
            </p>
          </form>
        </div>
      </section>

      {/* ---------- TITLE COMPANY CALLOUT (Phase 2) ---------- */}
      <section className="py-16 bg-white border-t border-[#e6dec7]" data-testid="hoa-title-callout">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="rounded-lg overflow-hidden border border-[#e6dec7] shadow-sm grid md:grid-cols-5">
            <div className="md:col-span-3 p-8 sm:p-10" style={{ backgroundColor: '#faf8f4' }}>
              <Eyebrow>For Title Companies &amp; Closing Attorneys</Eyebrow>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold leading-tight" style={{ color: DARK }}>
                Need an HOA payoff letter?<br />
                <span style={{ color: GOLD }}>Request one online in two minutes.</span>
              </h2>
              <p className="mt-4 text-[15px] text-gray-700 leading-relaxed">
                Standard turnaround is 1 business day for any Bespoke-managed community. Rush service available for same-day closings.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-gray-700">
                <li className="flex items-center gap-2"><Check className="h-4 w-4" style={{ color: GOLD }} /> Signed PDF emailed as soon as it's ready</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4" style={{ color: GOLD }} /> Full payoff — balance + transfer fee + working capital</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4" style={{ color: GOLD }} /> Pay the estoppel fee by card at submission</li>
              </ul>
            </div>
            <div className="md:col-span-2 p-8 sm:p-10 flex flex-col items-start justify-center text-white" style={{ backgroundColor: DARK }}>
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: GOLD }}>Request a Payoff</div>
              <div className="text-3xl font-bold mb-1">$200</div>
              <div className="text-xs text-white/60 mb-6">Standard · $300 for rush</div>
              <a
                href="/title/register"
                className="inline-flex items-center justify-center gap-2 text-white px-5 py-3 rounded-md font-semibold w-full"
                style={{ backgroundColor: GOLD }}
                data-testid="hoa-title-register-cta"
              >
                Create a Title Account <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/title/login"
                className="text-xs text-white/70 hover:text-white underline mt-3"
                data-testid="hoa-title-login-link"
              >
                Already registered? Sign in →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- CLOSING ---------- */}
      <section className="py-24 bg-white" data-testid="hoa-closing">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Eyebrow>Accountability</Eyebrow>
          <blockquote
            className="mt-6 text-2xl sm:text-3xl lg:text-4xl leading-snug font-medium italic"
            style={{ color: DARK }}
          >
            &ldquo;Every community we take on, we take on carefully — because every board, every budget,
            and every homeowner deserves management that treats their community like it matters.&rdquo;
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="w-10 h-px" style={{ background: GOLD }} />
            <div>
              <div className="font-bold" style={{ color: DARK }}>Benjamin Wright</div>
              <div className="text-xs tracking-widest uppercase text-gray-500 mt-0.5">
                Principal Broker / Owner
              </div>
            </div>
            <div className="w-10 h-px" style={{ background: GOLD }} />
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#hoa-proposal"
              className="inline-flex items-center justify-center gap-2 text-white px-6 py-3.5 rounded-md font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: GOLD }}
            >
              Request a Proposal <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="tel:9013904414"
              className="inline-flex items-center justify-center gap-2 border px-6 py-3.5 rounded-md font-semibold transition-colors"
              style={{ borderColor: `${GOLD}80`, color: DARK }}
            >
              Call 901-390-4414
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HoaManagement;
